/**
 * About page copy.
 *
 * Written for a new firm: no founding year, no team size, no client count.
 * The credibility argument is the working method and the leadership team's
 * direct involvement, both of which are true and verifiable.
 *
 * CALGARY LEADS. Calgary is the primary market and is named first wherever
 * both cities appear; Lahore follows as the delivery office.
 */

export const about = {
  hero: {
    eyebrow: 'About us',
    headline: 'A Calgary software team that ships',
    subcopy:
      'Falcon Solutions is a custom software development company based in Calgary, Alberta, with a delivery team in Lahore. We build web and mobile products, connect the systems you already pay for, and put AI to work where it genuinely earns its cost.',
  },

  story: {
    eyebrow: 'Why we started',
    heading: 'Most software problems are communication problems',
    body: [
      'Falcon Solutions was founded on a straightforward observation: the gap between what a client asks for and what gets built is where budgets disappear. Requirements pass through account managers, get translated into tickets, and arrive at a developer stripped of the context that made them make sense.',
      'So we removed the layers. You talk directly to the people building your software. When something is ambiguous, we ask the same day rather than guessing and invoicing the rework. When an approach will not work, we say so before starting rather than after.',
      'That model puts a ceiling on how much work we take at once, and we accept that trade. It is the reason the work gets done properly.',
    ],
    pullQuote: 'You talk to the people writing the code. Everything else follows from that.',
  },

  values: {
    eyebrow: 'What we hold to',
    heading: 'Four commitments',
    items: [
      {
        title: 'Say the difficult thing early',
        body: 'A timeline that will slip, a feature that will not pay for itself, an approach that will not scale. You hear it while there is still time to act on it.',
      },
      {
        title: 'Build for the handover',
        body: 'Every project is written as if another team will inherit it next month. Typed, documented, and free of undocumented shortcuts.',
      },
      {
        title: 'Measure before claiming',
        body: 'Performance numbers, accuracy rates, test coverage: if we state it, we measured it and can show you the method.',
      },
      {
        title: 'Own the mistakes',
        body: 'Bugs we introduce get fixed on our time. That is not a policy so much as the baseline for taking the work seriously.',
      },
    ],
  },

  metaphor: {
    eyebrow: 'The name',
    heading: 'Vision, precision, speed',
    body: 'A falcon sees detail from altitude, commits to a target, and does not waste the dive. It is a reasonable description of how good engineering works: understand the whole system, choose the right intervention, execute it cleanly. The mark in our logo is a falcon in that dive.',
  },

  locations: {
    eyebrow: 'Where we are',
    heading: 'Calgary first, with cover across the clock',
    body: 'We are based in Calgary, Alberta, and run a second development team in Lahore. Clients in Canada and the US get a full working-hours overlap with people who can answer for the code, and the build keeps moving while one side sleeps. Everything runs through shared documentation, so nothing depends on catching one person online.',
  },

  cta: {
    heading: 'Work with us',
    body: 'If the way we work sounds like a fit, tell us what you are building.',
    button: { label: 'Start a conversation', href: '/contact' },
  },
} as const;
